import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";

import { Dropdown } from "@/components";
import { Subtitles } from "@/icons";
import { setIsSubtitlesEnabled, setPreferredSubtitleLanguage } from "@/utils";
import { useTranslate } from "@/providers/context";

interface SubtitlesDropdownProps {
  subtitleTracks?: HTMLTrackElement[];
  activeSubtitleId: string | null;
  setActiveSubtitleId: Dispatch<SetStateAction<string | null>>;
  defaultSubtitleId?: string;
}

export const SubtitlesDropdown = ({
  subtitleTracks = [],
  activeSubtitleId,
  setActiveSubtitleId,
  defaultSubtitleId,
}: SubtitlesDropdownProps) => {
  const [open, setOpen] = useState(false);
  const { translate } = useTranslate();

  const handleSubtitleChange = useCallback(
    (id: string | null, srclang?: string) => {
      setIsSubtitlesEnabled(!!id);
      setActiveSubtitleId(id);
      if (srclang && srclang !== "unknown") {
        setPreferredSubtitleLanguage(srclang);
      }
      setOpen(false);
    },
    [setActiveSubtitleId, setOpen],
  );

  const { defaultOptions, remainingOptions } = useMemo(
    () =>
      subtitleTracks.reduce(
        (acc, track) => {
          const option = {
            text: track.label,
            onClick: () => handleSubtitleChange(track.id, track.srclang),
            selected: track.id === activeSubtitleId,
          };

          if (track.id === defaultSubtitleId) {
            acc.defaultOptions.push(option);
          } else {
            acc.remainingOptions.push(option);
          }
          return acc;
        },
        {
          defaultOptions: [
            {
              text: translate("Off"),
              onClick: () => handleSubtitleChange(null),
              selected: activeSubtitleId === null,
            },
          ],
          remainingOptions: [] as Array<{
            text: string;
            onClick: () => void;
            selected: boolean;
          }>,
        },
      ),
    [
      subtitleTracks,
      activeSubtitleId,
      defaultSubtitleId,
      handleSubtitleChange,
      translate,
    ],
  );

  const optionsGroups = useMemo(() => {
    const groups = [defaultOptions];
    if (remainingOptions.length > 0) {
      const sortedRemainingOptions = remainingOptions.sort((a, b) =>
        a.text.localeCompare(b.text),
      );
      groups.push(sortedRemainingOptions);
    }
    return groups;
  }, [defaultOptions, remainingOptions]);

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      contentProps={{
        align: "end",
        side: "top",
        sideOffset: 10,
        alignOffset: -3,
      }}
      className="max-h-[400px] min-w-[160px] overflow-y-auto"
      heading={translate("Subtitles")}
      optionsGroups={optionsGroups}
      customContent={
        subtitleTracks.length === 0 && (
          <div className="p2-medium p-4 text-item-secondary">
            {translate("No Subtitles")}
          </div>
        )
      }
    >
      <Subtitles className="h-6 w-6 hover:opacity-85" />
    </Dropdown>
  );
};
